import { useDataFormatter } from "@/composable/data-formatter";
import { DataDefinition } from "@soft-db/data-formatter";
import {
	Audit,
	AuditAction,
	AuditChange,
	AuditEntry,
	AuditTarget,
} from "@soft-db/monitoring-api-client";
import { For, Match, Show, Switch, fromBase64 } from "@soft-db/soft-utils";
import { SoftDialog, SoftTypography, tw } from "@soft-db/ui";
import { PropType } from "vue";
import { RouterLink } from "vue-router";
import AppTextTooltip from "../common/AppTextTooltip";
import { isLinkChange, useAuditLinkResolver } from "./audit-link-resolver";
import { useAuditNameResolver } from "./audit-name-resolver";
import { useAuditValueResolver } from "./audit-value-resolver";
import AuditTargetDisplay from "./AuditTargetDisplay";

export default defineComponent({
	name: "AuditDiffViewer",
	props: {
		log: Object as PropType<Audit>,
	},
	setup(props) {
		const logModel = useVModel(props, "log");

		const open = computed({
			get: () => !!props.log,
			set: (open) => {
				if (!open) {
					logModel.value = undefined;
				}
			},
		});

		const changes = computed(() => Object.entries(logModel.value?.changes || {}));

		const simpleChanges = computed(() =>
			changes.value.filter(
				(changes): changes is [string, AuditChange] => !Array.isArray(changes[1])
			)
		);

		const nestedChanges = computed(() =>
			changes.value.filter((changes): changes is [string, AuditEntry[]] =>
				Array.isArray(changes[1])
			)
		);

		const { resolveTargetName, resolveActionName } = useAuditNameResolver();

		const targetHeader = computed(() => {
			if (!props.log || !props.log.target) return "";

			return `${resolveTargetName(props.log.target.type)} - ${resolveActionName(props.log.action)}`;
		});

		const target = computed(() => props.log?.target?.type || "");

		return () => (
			<SoftDialog v-model={open.value} class="w-200">
				<>
					{/* <AuditDetails /> */}
					{props.log && <AuditTargetDisplay audit={props.log} />}
					<div class="flex flex-col gap-2">
						<Show when={simpleChanges.value.length !== 0}>
							<SoftTypography tag="h3">{targetHeader.value}</SoftTypography>
							<For each={simpleChanges.value}>
								{([name, changes]) => (
									<AuditChangeViewer target={target.value} name={name} change={changes} />
								)}
							</For>
						</Show>

						<For each={nestedChanges.value}>
							{([name, changes]) => (
								<AuditEntryViewer target={target.value} name={name} entries={changes} />
							)}
						</For>
					</div>
				</>
			</SoftDialog>
		);
	},
});

const AuditEntryViewer = defineComponent({
	name: "AuditEntry",
	props: {
		target: {
			type: String as PropType<AuditTarget | "">,
			required: true,
		},
		name: {
			type: String,
			required: true,
		},
		entries: {
			type: Array as PropType<AuditEntry[]>,
			required: true,
		},
	},
	setup(props) {
		const { resolveFieldsName, resolveCollectionActionName } = useAuditNameResolver();

		return () => (
			<For each={props.entries}>
				{(entry) => (
					<div>
						<SoftTypography tag="h3">
							<span class="capitalize">{resolveFieldsName(props.name)}</span> -{" "}
							{resolveCollectionActionName(entry.action)}
						</SoftTypography>
						<div>
							<>
								<Show when={entry.id}>
									<AuditTargetViewer
										target={props.target}
										name={props.name}
										id={entry.id}
										action={entry.action}
									/>
								</Show>
								<For each={Object.entries(entry.changes || {})}>
									{([name, change]) => (
										<AuditChangeViewer target={props.target} name={name} change={change} />
									)}
								</For>
							</>
						</div>
					</div>
				)}
			</For>
		);
	},
});

const AuditTargetViewer = defineComponent({
	name: "AuditTargetViewer",
	props: {
		id: {
			type: String,
			required: true,
		},
		target: {
			type: String as PropType<AuditTarget | "">,
			requried: true,
		},
		name: {
			type: String,
			required: true,
		},
		action: {
			type: String as PropType<AuditAction>,
			required: true,
		},
	},
	setup(props) {
		const { resolveFieldsName } = useAuditNameResolver();

		return () =>
			isLinkChange(props.name) && (
				<div class="flex items-center justify-between gap-2">
					<SoftTypography tag="code">{resolveFieldsName(props.name)}</SoftTypography>
					<div class="border-b-neutral-content/60 grow border-b-2 border-dashed" />

					<LinkDiff
						class="max-w-[50%]"
						name={props.name}
						change={{
							before: props.action === "deleted" ? props.id : undefined,
							after: props.action !== "deleted" ? props.id : undefined,
						}}
					/>
				</div>
			);
	},
});

const AuditChangeViewer = defineComponent({
	name: "AuditChangeViewer",
	props: {
		target: {
			type: String as PropType<AuditTarget | "">,
			required: true,
		},
		name: {
			type: String,
			required: true,
		},
		change: {
			type: Object as PropType<AuditChange>,
			required: true,
		},
	},
	setup(props) {
		const { resolveFieldsName } = useAuditNameResolver();
		const { resolveValueName } = useAuditValueResolver();

		const fieldName = computed(() => props.name);
		const isIdentityChange = computed(() => fieldName.value.startsWith("identity"));

		function resolveChangeValue(change?: string | null) {
			if (change == null) return null;

			return resolveValueName(props.target, props.name, change);
		}

		const change = computed<AuditChange>(() => ({
			before: !isIdentityChange.value ? resolveChangeValue(props.change.before) : undefined,
			after: resolveChangeValue(props.change.after),
		}));

		return () => (
			<div class="flex items-center justify-between gap-2">
				<SoftTypography tag="code">{resolveFieldsName(props.name)}</SoftTypography>
				<div class="border-b-neutral-content/60 grow border-b-2 border-dashed" />

				<div class="max-w-[50%]">
					<Switch>
						<Match when={isLinkChange(props.name)}>
							<LinkDiff name={props.name} change={props.change} />
						</Match>
						<Match default>
							<StrDiff change={change.value} />
						</Match>
					</Switch>
				</div>
			</div>
		);
	},
});

const LinkDiff = defineComponent({
	name: "LinkDiff",
	props: {
		name: {
			type: String,
			required: true,
		},
		change: {
			type: Object as PropType<AuditChange>,
			required: true,
		},
	},
	setup(props) {
		const before = useAuditLinkResolver(() => ({
			name: props.name,
			value: props.change.before,
		}));

		const after = useAuditLinkResolver(() => ({
			name: props.name,
			value: props.change.after,
		}));

		function removedClasses(isLink: boolean) {
			const classes: string[] = [tw`text-error/70 line-through`];
			if (isLink) {
				classes.push(tw`hover:[text-decoration:underline_line-through]`);
			}

			return classes.join(" ");
		}

		function addedClasses(isLink: boolean) {
			const classes: string[] = [];
			if (isLink) {
				classes.push(tw`text-secondary hover:underline`);
			}

			return classes.join(" ");
		}

		const beforeClasses = computed(() => {
			return removedClasses(before.value?.location != null);
		});

		const afterClasses = computed(() => {
			return addedClasses(after.value?.location != null);
		});

		return () => (
			<div class="flex gap-2">
				<Show when={before.value}>
					{(before) => (
						<AppTextTooltip
							as={before.location ? RouterLink : "div"}
							class={beforeClasses.value}
							to={before.location}
							value={before.text}
						/>
					)}
				</Show>

				<Show when={after.value}>
					{(after) => (
						<AppTextTooltip
							as={after.location ? RouterLink : "div"}
							class={afterClasses.value}
							to={after.location}
							value={after.text}
						/>
					)}
				</Show>
			</div>
		);
	},
});

const StrDiff = defineComponent({
	name: "StrDiff",
	props: {
		change: {
			type: Object as PropType<AuditChange>,
			required: true,
		},
	},
	setup(props) {
		const { generateDataLabel } = useDataFormatter();

		function isBase64String(str: any) {
			try {
				return !!fromBase64(str);
			} catch {
				return false;
			}
		}

		function isDataDefinition(obj: any): obj is DataDefinition {
			return !!obj && typeof obj === "object" && "type" in obj && "data" in obj;
		}

		function safeBase64(str: string) {
			try {
				return fromBase64(str);
			} catch {
				return "{}";
			}
		}

		function safeJSONParse(str: string) {
			try {
				return JSON.parse(str);
			} catch {
				return {};
			}
		}

		function parseChange(change: string | undefined | null) {
			if (!change) return "";

			if (isBase64String(change)) {
				const parsed = safeJSONParse(safeBase64(change));
				if (isDataDefinition(parsed)) {
					return `${generateDataLabel(parsed)} (${parsed.unit})`;
				}
			}

			return change;
		}

		const before = computed(() => parseChange(props.change.before));
		const after = computed(() => parseChange(props.change.after));

		return () => (
			<div class="flex gap-2">
				<Show when={before.value}>
					<AppTextTooltip class="text-error/70 line-through" value={before.value} />
				</Show>

				<Show when={after.value}>
					<AppTextTooltip value={after.value} />
				</Show>
			</div>
		);
	},
});
